import "./customInput.scss";

import classnames from "classnames";
import type { ChangeEvent, InputHTMLAttributes } from "react";
import { FC, useEffect, useId, useRef, useState } from "react";
import { Alert, Form, InputGroup } from "react-bootstrap";

interface IProps extends InputHTMLAttributes<HTMLInputElement> {
  handleChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  inputValue: string;
  placeholder: string;
  type?: string;
  label?: string;
  name?: string;
  autoFocus?: boolean;
  required?: boolean;
  errorMessage?: { error: string };
  className?: string;
  maxLength?: number;
}

const CustomInput: React.FC<IProps> = ({
  handleChange,
  inputValue,
  placeholder,
  label,
  name,
  autoFocus,
  type,
  required,
  disabled,
  errorMessage,
  className,
  maxLength,
  onBlur,
}) => {
  const inputClassname = classnames({
    "w-100": true,
    "custom-input-element": true,
    "mt-5": !label,
    rounded: true,
  });

  const textAreaClassName = classnames({
    "w-100": true,
    "custom-input-element": true,
    "mt-5": !label,
    rounded: true,
    "h-200": true,
  });

  const [internalErrorMessage, setInternalErrorMessage] = useState<
    string | null
  >(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const id = useId();

  useEffect(() => {
    if (errorMessage?.error) {
      setInternalErrorMessage(errorMessage.error);
      return;
    }
    setInternalErrorMessage("");
  }, [errorMessage?.error]);

  const labelClassname = classnames({
    "d-block": true,
    // 'mt-2': true,
    "fs-7": true,
    "mt-0": true,
    required,
  });

  const onInput = (e: ChangeEvent<HTMLInputElement>) => {
    setInternalErrorMessage("");
    handleChange && handleChange(e);
  };

  useEffect(() => {
    setTimeout(() => {
      if (autoFocus) inputRef.current?.focus();
    }, 100);
  }, [autoFocus]);

  return (
    <>
      <InputGroup
        className={`custom-input-container mb-4 gap-1 ${
          className ? className : ""
        }`}
      >
        {label && (
          <Form.Label className={labelClassname} htmlFor={id}>
            {label}
          </Form.Label>
        )}
        {type !== "textarea" ? (
          <Form.Control
            className={inputClassname}
            onChange={onInput}
            value={inputValue}
            type="text"
            placeholder={placeholder}
            name={name}
            id={id}
            disabled={disabled}
            autoComplete="off"
            ref={inputRef}
            maxLength={maxLength}
            onBlur={onBlur}
          />
        ) : (
          <Form.Control
            className={textAreaClassName}
            onChange={handleChange}
            value={inputValue}
            as="textarea"
            rows={5}
            placeholder={placeholder}
            name={name}
            id={id}
            autoFocus={autoFocus}
            style={{ height: "100%" }}
            maxLength={maxLength}
          />
        )}
      </InputGroup>
      {internalErrorMessage && (
        <Alert className="custom-input-alert" variant="danger">
          {internalErrorMessage}
        </Alert>
      )}
    </>
  );
};

export { CustomInput };
